import React from 'react';
import { Key, X, Sparkles, Trash2, Check, Loader2 } from 'lucide-react';

export interface KeyExtractionJob {
  id: string;
  snapshot: string;
  status: 'queued' | 'processing' | 'done' | 'error';
  results?: any[];
  error?: string;
}

export interface KeyExtractionPanelProps {
  keyExtractionJobs: KeyExtractionJob[];
  onCancel: () => void;
  onDeleteJob: (jobId: string) => void;
  onFinishSelection: () => void;
}

export const KeyExtractionPanel: React.FC<KeyExtractionPanelProps> = ({
  keyExtractionJobs,
  onCancel,
  onDeleteJob,
  onFinishSelection,
}) => (
  <div style={{
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 360,
    maxHeight: 500,
    background: 'var(--bg-primary)',
    borderRadius: 16,
    boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
    zIndex: 100004,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '2px solid #3b82f6',
  }}>
    {/* Header */}
    <div style={{
      padding: '14px 16px',
      borderBottom: '1px solid var(--border-color)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: 'linear-gradient(135deg, var(--blue-tint-light) 0%, var(--bg-secondary) 100%)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Key size={16} style={{ color: '#3b82f6' }} />
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>
          Power Legend Extraction
        </span>
      </div>
      <button
        onClick={onCancel}
        style={{ background: 'transparent', border: 'none', color: 'var(--text-placeholder)', cursor: 'pointer', padding: 4 }}
        title="Cancel"
      >
        <X size={16} />
      </button>
    </div>

    {/* Instruction or Job cards */}
    <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
      {keyExtractionJobs.length === 0 ? (
        <div style={{
          padding: '24px 12px',
          textAlign: 'center',
          color: 'var(--text-muted)',
          fontSize: 13,
          lineHeight: 1.6,
        }}>
          <Key size={28} style={{ color: 'var(--border-strong)', marginBottom: 8 }} />
          <p style={{ margin: 0, fontWeight: 500, color: 'var(--text-secondary)' }}>
            Select legend areas on the PDF
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 12 }}>
            Draw a rectangle around each legend section. You can add multiple areas if the legend is split.
          </p>
        </div>
      ) : (
        keyExtractionJobs.map((job, idx) => (
          <div
            key={job.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 6px',
              borderBottom: idx < keyExtractionJobs.length - 1 ? '1px solid var(--border-subtle)' : 'none',
            }}
          >
            <img
              src={job.snapshot}
              alt={`Area ${idx + 1}`}
              style={{
                width: 64,
                height: 44,
                objectFit: 'cover',
                borderRadius: 6,
                border: '1px solid var(--border-color)',
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>
                Area {idx + 1}
              </div>
              <div style={{ fontSize: 11, color: job.status === 'error' ? '#ef4444' : 'var(--text-placeholder)', marginTop: 2 }}>
                {job.status === 'queued' && 'Ready to extract'}
                {job.status === 'processing' && 'Extracting...'}
                {job.status === 'done' && `${job.results?.length || 0} symbols found`}
                {job.status === 'error' && (job.error || 'Failed')}
              </div>
            </div>
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {job.status === 'queued' && <Check size={16} style={{ color: 'var(--text-placeholder)' }} />}
              {job.status === 'processing' && <Loader2 size={16} className="spin" style={{ color: '#3b82f6' }} />}
              {job.status === 'done' && <Check size={16} style={{ color: 'var(--green-text)' }} />}
              {job.status === 'error' && <X size={16} style={{ color: '#ef4444' }} />}
            </div>
            <button
              onClick={() => onDeleteJob(job.id)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--border-strong)',
                cursor: 'pointer',
                padding: 4,
                flexShrink: 0,
                borderRadius: 4,
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; }}
              title="Remove this area"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))
      )}
    </div>

    {/* Footer with Extract Key button */}
    <div style={{
      padding: '12px 16px',
      borderTop: '1px solid var(--border-color)',
      display: 'flex',
      gap: 8,
      background: 'var(--bg-secondary)',
    }}>
      <div style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
        {keyExtractionJobs.length === 0
          ? 'Draw a rectangle on the PDF'
          : 'Select more areas or click Extract'}
      </div>
      <button
        onClick={onFinishSelection}
        disabled={keyExtractionJobs.length === 0}
        style={{
          padding: '8px 18px',
          background: keyExtractionJobs.length > 0
            ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
            : 'var(--border-strong)',
          border: 'none',
          borderRadius: 8,
          color: '#ffffff',
          fontSize: 13,
          fontWeight: 600,
          cursor: keyExtractionJobs.length > 0 ? 'pointer' : 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          boxShadow: keyExtractionJobs.length > 0
            ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none',
        }}
      >
        <Sparkles size={14} />
        {keyExtractionJobs.length > 0 ? `Extract Key (${keyExtractionJobs.length})` : 'Extract Key'}
      </button>
    </div>
  </div>
);
